<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { AutoForm } from '@/ui-plus/auto-form'
import { toast } from '@/ui-plus/sonner'
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import { toTypedSchema } from "@vee-validate/zod";
import { authGlobalApiClient } from '@api-client/auth_global';
import { useForm } from "vee-validate";
import { z } from "zod";
import { Loader2 } from 'lucide-vue-next';
import { showTurnstile } from '@/ui-plus/dialog/showTurnstile';
import { ref } from 'vue';
import { useI18n } from "vue-i18n";
import ClaimEmailCodeForm from '@auth-frontend-vue/components/forms/ClaimEmailCodeForm.vue';

const { t } = useI18n();

withDefaults(defineProps<{
    showCancel?: boolean;
}>(), {
    showCancel: true,
});

const emit = defineEmits<{
    onSuccess: [],
    onCancel: [],
    onError: [error: unknown],
}>();

const schema = z.object({
    email: z.string().email().max(200),
})

const form = useForm({
    validationSchema: toTypedSchema(schema)
});
const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form);

const otpToken = ref<string | undefined>();
const sentTo = ref('');

async function onSendOtpSubmit(values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        const { data, error } = await (await authGlobalApiClient.user['send-claim-email'].$post({
            form: {
                email: values.email,
                turnstile: await showTurnstile(import.meta.env.PUBLIC_TURNSTILE_SITE_KEY),
            },
        })).json()
        if (error) {
            handleError(error, form);
        } else {
            otpToken.value = data;
            sentTo.value = values.email;
            toast.success(t('claimEmailCodeSent'));
        }
    })
}
</script>

<template>
    <div v-if="!otpToken">
        <AutoForm class="space-y-6"
                  :schema="schema"
                  :form="form"
                  @submit="onSendOtpSubmit"
                  :field-config="{
                    email: {
                        label: t('yourEmail'),
                        inputProps: {
                            autofocus: true,
                            autocomplete: 'email',
                            type: 'email',
                        },
                    },
                }">
            <div class="text-sm font-medium text-destructive"
                 v-if="globalError">
                {{ globalError }}
            </div>
            <div>
                <Button type="submit"
                        :disabled="isLoading"
                        class="w-full my-6">
                    <Loader2 v-if="isLoading"
                             class="animate-spin" />
                    {{ t('sendClaimEmailCode') }}
                </Button>
            </div>
        </AutoForm>
        <Button v-if="showCancel"
                variant="secondary"
                @click="emit('onCancel')"> {{ t('cancel') }}
        </Button>
    </div>
    <div v-else
         class="space-y-4 py-2">
        <p class="text-sm text-muted-foreground text-center">
            {{ t('claimEmailEnterCode') }}
            <br />
            <span class="font-medium text-foreground">{{ sentTo }}</span>
        </p>
        <ClaimEmailCodeForm :otp-token="otpToken"
                            @on-success="emit('onSuccess')" />
        <Button variant="secondary"
                class="w-full"
                @click="otpToken = undefined">
            {{ t('useDifferentEmail') }}
        </Button>
    </div>
</template>
